import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { trpcClient } from '@/lib/trpc';
import { formatDate } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Plus, FileText, Calendar, Clock, Loader2, Pencil, Trash2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface Props {
    userId: string;
    hideHeader?: boolean;
    onEdit?: (contract: any) => void;
    onAdd?: () => void;
}

export function EmployeeContractList({ userId, hideHeader, onEdit, onAdd }: Props) {
    const queryClient = useQueryClient();
    const [showDelete, setShowDelete] = useState(false);
    const [deleteId, setDeleteId] = useState<string | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const { data, isLoading } = useQuery({
        queryKey: ['employee-contracts', userId],
        queryFn: async () => trpcClient.employeeContracts.list.query({
            userId,
            page: 1,
            limit: 100,
        }),
    });

    if (isLoading) {
        return (
            <div className="space-y-4">
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-24 w-full" />
            </div>
        );
    }

    const contracts = data?.items || [];

    const getContractTypeLabel = (type: string) => {
        const labels: Record<string, string> = {
            'PROBATION': 'Thử việc',
            'DEFINITE': 'Xác định thời hạn',
            'INDEFINITE': 'Không xác định thời hạn',
            'SEASONAL': 'Thời vụ'
        };
        return labels[type] || type;
    };

    const getStatusColor = (status: string) => {
        const colors: Record<string, string> = {
            'ACTIVE': 'bg-emerald-100 text-emerald-800 border-emerald-200',
            'EXPIRED': 'bg-rose-100 text-rose-800 border-rose-200',
            'TERMINATED': 'bg-slate-100 text-slate-800 border-slate-200',
            'DRAFT': 'bg-amber-100 text-amber-800 border-amber-200'
        };
        return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
    };

    const handleDelete = async () => {
        if (!deleteId) return;
        setIsDeleting(true);
        try {
            await trpcClient.employeeContracts.delete.mutate({ id: deleteId });
            
            setShowDelete(false);
            setDeleteId(null);
            await queryClient.invalidateQueries({ queryKey: ['employee-contracts', userId] });
        } catch (error: any) {
            toast.error(error.message || 'Lỗi khi xoá hợp đồng');
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <div className="space-y-4">
            {!hideHeader && (
                <div className="flex justify-between items-center">
                    <h3 className="text-sm font-semibold text-slate-800">Danh sách hợp đồng ({contracts.length})</h3>
                    <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={onAdd}>
                        <Plus className="w-4 h-4 mr-2" /> Thêm hợp đồng
                    </Button>
                </div>
            )}

            {contracts.length === 0 ? (
                <div className="text-center p-8 border border-dashed rounded-lg bg-slate-50 text-slate-500">
                    Chưa có hợp đồng nào
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {contracts.map((contract: any) => (
                        <Card key={contract.id} className="border-slate-200 shadow-sm hover:border-indigo-200 transition-colors group">
                            <CardContent className="p-4">
                                <div className="flex justify-between items-start mb-3">
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 rounded bg-indigo-50 flex items-center justify-center text-indigo-600 cursor-pointer" onClick={() => onEdit?.(contract)}>
                                            <FileText className="w-4 h-4" />
                                        </div>
                                        <div className="cursor-pointer" onClick={() => onEdit?.(contract)}>
                                            <div className="font-semibold text-sm text-slate-900 group-hover:text-indigo-600 transition-colors">
                                                {contract.contractNumber}
                                            </div>
                                            <div className="text-xs text-slate-500">
                                                {getContractTypeLabel(contract.contractType || contract.type)}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex flex-col items-end gap-2">
                                        <Badge variant="outline" className={getStatusColor(contract.status)}>
                                            {contract.status}
                                        </Badge>
                                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <Button variant="ghost" size="icon" className="h-6 w-6 text-slate-500 hover:text-indigo-600" onClick={() => onEdit?.(contract)}>
                                                <Pencil className="h-3.5 w-3.5" />
                                            </Button>
                                            <Button variant="ghost" size="icon" className="h-6 w-6 text-slate-500 hover:text-rose-600 hover:bg-rose-50" onClick={() => { setDeleteId(contract.id); setShowDelete(true); }}>
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-2 text-xs text-slate-600 mt-2">
                                    <div className="flex items-center gap-1.5">
                                        <Calendar className="w-3.5 h-3.5 text-slate-400" />
                                        Bắt đầu: <span className="font-medium text-slate-900">{formatDate(contract.startDate)}</span>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <Clock className="w-3.5 h-3.5 text-slate-400" />
                                        Kết thúc: <span className="font-medium text-slate-900">{contract.endDate ? formatDate(contract.endDate) : '—'}</span>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            <AlertDialog open={showDelete} onOpenChange={setShowDelete}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Xoá hợp đồng?</AlertDialogTitle>
                        <AlertDialogDescription>Hành động này không thể hoàn tác.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Huỷ</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} disabled={isDeleting} className="bg-rose-600 hover:bg-rose-700">
                            {isDeleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Xoá
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
